import cn from 'classnames';
import * as api from '../api';
import { storeAPI } from '../config';
import { userLocationURL } from '../config/SearchAPIConfig';
import ErrorBox from '../generic/ErrorBox';
import Icon from '../generic/Icon';
import InfoBox from '../generic/InfoBox';
import preventDefault from '../utils/preventDefault';
import { useAsyncAction } from '../utils/useAsyncAction';
import SearchBox from './SearchBox';
import * as style from './StoreLocatorForm.module.css';

export interface Coords {
    latitude: number;
    longitude: number;
}

export interface Props {
    onLocationChange(pos: Coords): Promise<void>;
    onItemSelected(id: string, name: string, geoPoint: Coords): void;
    className?: string;
}

export default function StoreLocatorForm({ onLocationChange, onItemSelected, className }: Props) {
    const [search, isSearching, error, , resetError] = useAsyncAction(async (query: string) => {
        resetFail();
        if (!query) return;
        const result = await locationSearch(query);
        await onLocationChange(result);
    });

    const [fetchGeoPosition, isFetchingGeoPosition, fail, , resetFail] = useAsyncAction(async () => {
        resetError();
        if (!navigator.geolocation) {
            throw 'Du benytter en nettleser som ikke støtter posisjonering.';
        }

        const position = await getCurrentPosition();
        await onLocationChange(position);
    });

    return (
        <>
            <div className={cn(style.storeFinderSearch, className)}>
                <SearchBox
                    isSearching={isSearching || isFetchingGeoPosition}
                    onSearch={search}
                    onItemSelected={onItemSelected}
                    autocomplete={autoComplete}
                />
                <button type="button" className={style.locateButton} onClick={preventDefault(fetchGeoPosition)} aria-label="Finn nærmeste Vinmonopol">
                    <Icon className={style.iconLocationPin} />
                </button>
            </div>

            <InfoBox message={fail as string} />
            <ErrorBox errors={error} />
        </>
    );
}

interface Response {
    stores: {
        displayName: string;
        id: string;
        geoPoint: Coords;
    }[];
}

async function autoComplete(input: string) {
    const { stores } = await api.getAnonymously<Response>(storeAPI.autocompleteURL(input.toLowerCase()));
    return stores.map(({ displayName, id, geoPoint }) => ({
        value: id,
        label: displayName,
        geoPoint,
    }));
}

async function locationSearch(query: string) {
    return api.getAnonymously<Coords>(userLocationURL(), { query: query.toLowerCase() });
}

async function getCurrentPosition() {
    return await new Promise<Coords>((res, rej) =>
        navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude, longitude } }) => {
                res({ latitude, longitude });
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        return rej(
                            'Vi har ikke tilgang til din posisjon. Hvis du ønsker å gi oss denne tilgangen, bør du sjekke tilgang til lokasjonstjenester i nettleserens innstillinger eller i systeminnstillinger.',
                        );
                    case error.POSITION_UNAVAILABLE:
                        return rej('Din posisjon er ikke tilgjengelig.');
                    case error.TIMEOUT:
                        return rej(
                            'Vi kunne ikke finne din posisjon. Det kan hende du har skrudd av lokasjonstjenester i systeminnstillinger. Vennligst prøv igjen.',
                        );
                    default:
                        return rej('Det oppstod en feil ved henting av din posisjon.');
                }
            },
            { timeout: 5000 },
        ),
    );
}
